import React, { useEffect, useImperativeHandle, useRef } from 'react';
import { Form } from 'antd';
import GenericTab from './GenericTab';
import { useSegmentation } from '../../../hooks/useSegmentation';

export const SEGMENTATION_NAMES = [ 'brandIds', 'segmentIds', 'localeIds', 'employeeLocaleIds',
    'companyRelationshipIds', 'productIds', 'learningProfileIds' ];

/**
 * Pulls the segmentation ids off a fetched item and into form values. Spread this into whatever
 * formatDataToForm returns - useCreateEditGeneric only hands formatFormToData the fields listed
 * in initialValues, so segmentation has to be in there to survive a save.
 * @param {Object} item fetched item holding the segmentation id arrays
 */
export function formatSegmentationForm( item ) {
    return Object.fromEntries( SEGMENTATION_NAMES.map( n => [ n, item?.[ n ] ?? [] ] ) );
}

/**
 * Audience/segmentation tab for a create-edit page. Segmentation is not part of any patch input -
 * it is written with syncObjectRelationships - so the tab exposes an imperative `onSubmit` for the
 * page's `beforeSave` to await:
 *
 *     const audienceRef = useRef();
 *     async function beforeSave( { input } ) {
 *         await audienceRef.current?.onSubmit();
 *         return input;
 *     }
 *     <AudienceTab { ...tabProps } ref={ audienceRef } objectId={ id } objectType='resource'
 *         statuses={ tabStates.audience } onValidateCb={ s => onValidateTab( 'audience', s ) } />
 *
 * The field names are fixed - useSegmentation renders, initializes, and saves them by those names.
 *
 * @param {Object} ref exposes { onSubmit } which syncs segmentation for the item
 * @param {string} objectId id of the item being segmented
 * @param {string} objectType one of ['course','resource','curriculum','leaderboard-audience',...]
 */
const AudienceTab = ( {
    ref, form, item,
    title = 'Audience',
    updatedBrandsLocation = false,
    statuses, parentError, loading, onValidateCb, disabled
} ) => {

    const { loading: segmentationLoading, initializeSegmentation, saveSegmentation, renderSegmentation } =
        useSegmentation( form, { updatedBrandsLocation, defaultSelectBrand: false } );

    useImperativeHandle( ref, () => ( {
        onSubmit: ( { objectType, objectId } ) => saveSegmentation( form.getFieldsValue(), objectId, objectType )
    } ) );

    useEffect( () => {
        if ( !item || loading || segmentationLoading ) {
            return;
        }
        // re-apply useSegmentation's own defaults (first brand, all company relationships) on top
        // of whatever the item came back with
        initializeSegmentation( item );
    }, [ item, loading, segmentationLoading ] );

    return <GenericTab loading={ loading } parentError={ parentError } statuses={ statuses } title={ title }
        onValidateCb={ onValidateCb } form={ form } formNames={ SEGMENTATION_NAMES }>
        { ( { validateTab } ) => ( <>
            <FormValueWatcher form={ form } names={ SEGMENTATION_NAMES } onChange={ validateTab } />
            { renderSegmentation( loading, disabled ) }
        </> ) }
    </GenericTab>;
};

/**
 * useSegmentation renders its own Form.Items, so there is nowhere to hang an onChange telling the
 * tab it went dirty. Watch the values instead and re-run the tab's validation on a change.
 * Re-running is safe on programmatic updates too - GenericTab decides UNSAVED from isFieldsTouched,
 * which setFieldsValue and setFields leave alone.
 */
const FormValueWatcher = ( { form, names, onChange } ) => {

    const watched = Form.useWatch( values => JSON.stringify( names.map( n => values?.[ n ] ?? null ) ), form );
    const previous = useRef();

    useEffect( () => {
        if ( previous.current !== undefined && previous.current !== watched ) {
            onChange?.();
        }
        previous.current = watched;
    }, [ watched ] );

    return null;
};

export default AudienceTab;
